import { Component, Input, OnInit } from '@angular/core';
import { getEncumbranceNameByType } from '@core/util/encumbrance';
import {
	Encumbrance,
	EncumbranceActionType,
	EncumbranceType,
} from '@models/encumbrance';
import { EncumbrancesDispatch } from '@state/matter/encumbrances';
import {
	DropdownOption,
	DropdownResult,
} from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { ICON } from '@core/util/icon';
import { MatDialog } from '@angular/material/dialog';
import { ChangePriorityComponent } from 'src/app/private/sale/mortgages/manual/components/change-priority/change-priority.component';
import { startCase } from 'lodash';
import { MatterMortgagePriority } from '@models/mortgage';
import { deleteField } from '@angular/fire/firestore';
import { createId } from '@core/util/file';

const EncumbrancePriorities: DropdownOption<number>[] = [
	{ value: 1, name: '1st' },
	{ value: 2, name: '2nd' },
	{ value: 3, name: '3rd' },
	{ value: 4, name: '4th' },
	{ value: 5, name: '5th' },
];

@Component({
	selector: 'app-encumbrance-mortgage-details',
	templateUrl: './mortgage-details.component.html',
	standalone: false,
})
export class MortgageDetailsComponent {
	public ICON = ICON;
	public actionTypes = EncumbranceActionType;
	public encumbrancePriorities = EncumbrancePriorities;

	public selectedPriorityAfterClosingNumber: number;
	public selectedPriorityAfterClosingName: string;

	@Input() showPriorityWarning: string;
	@Input() set encumbrance(encumbrance: Encumbrance) {
		this._encumbrance = encumbrance;
		this.selectedPriorityAfterClosingNumber =
			this.encumbrance?.priorityAfterClosing;
		this.selectedPriorityAfterClosingName = this.encumbrancePriorities.find(
			(priority) => priority.value === this.encumbrance?.priorityAfterClosing,
		)?.name;
	}

	get encumbrance() {
		return this._encumbrance;
	}

	private _encumbrance;

	constructor(
		private encumbranceDispatch: EncumbrancesDispatch,
		private matDialog: MatDialog,
	) {}

	setObtainingDischargeStatement(value: boolean) {
		this.encumbranceDispatch.UpdateEncumbrance(
			this.encumbrance.id,
			'obtainingDischargeStatement',
			value,
		);
	}

	setLoanNumber(value: string) {
		this.encumbranceDispatch.UpdateEncumbrance(
			this.encumbrance.id,
			'loanNumber',
			value,
		);
	}

	setRegNumber(value: string) {
		this.encumbranceDispatch.UpdateEncumbrance(
			this.encumbrance.id,
			'registrationNumber',
			value,
		);
	}

	setMaturityDate(value: string) {
		this.encumbranceDispatch.UpdateEncumbrance(
			this.encumbrance.id,
			'maturityDate',
			value,
		);
	}

	setRegDate(value: string) {
		this.encumbranceDispatch.UpdateEncumbrance(
			this.encumbrance.id,
			'registrationDate',
			value,
		);
	}

	setPostponementFee(value: number) {
		this.encumbranceDispatch.UpdateEncumbrance(
			this.encumbrance.id,
			'postponementFee',
			value,
		);
	}

	setPriorityAfterClosing(value: MatterMortgagePriority) {
		this.encumbranceDispatch.UpdateEncumbrance(
			this.encumbrance.id,
			'priorityAfterClosing',
			value,
		);
	}

	setOutstandingAmount(value: number) {
		this.encumbranceDispatch.UpdateEncumbrance(
			this.encumbrance.id,
			'outstandingAmount',
			value,
		);
	}

	setPayPostponomentFeeFromTrustAccount(value: boolean) {
		this.encumbranceDispatch.UpdateEncumbrance(
			this.encumbrance.id,
			'payPostponomentFeeFromTrustAccount',
			value,
		);
	}

	setPostponeMortgage(value: boolean) {
		this.encumbranceDispatch.UpdateEncumbrance(
			this.encumbrance.id,
			'postponeMortgage',
			value,
		);
	}

	addRelatedInstrument() {
		this.encumbranceDispatch.UpdateEncumbrance(
			this.encumbrance.id,
			`relatedInstruments.${createId()}`,
			{ instrumentType: '', registrationNumber: '', registrationDate: '' },
		);
	}

	setInstrumentType(relatedInstrumentId: string, value: string) {
		this.encumbranceDispatch.UpdateEncumbrance(
			this.encumbrance.id,
			`relatedInstruments.${relatedInstrumentId}.instrumentType`,
			value,
		);
	}

	setInstrumentRegNumber(relatedInstrumentId: string, value: string) {
		this.encumbranceDispatch.UpdateEncumbrance(
			this.encumbrance.id,
			`relatedInstruments.${relatedInstrumentId}.registrationNumber`,
			value,
		);
	}

	setInstrumentRegDate(relatedInstrumentId: string, value: string) {
		this.encumbranceDispatch.UpdateEncumbrance(
			this.encumbrance.id,
			`relatedInstruments.${relatedInstrumentId}.registrationDate`,
			value,
		);
	}

	delete(relatedInstrumentId: string) {
		this.encumbranceDispatch.UpdateEncumbrance(
			this.encumbrance.id,
			`relatedInstruments.${relatedInstrumentId}`,
			deleteField(),
		);
	}

	trackByKey(index: string, item: { key: string }) {
		return item.key;
	}

	getEncumbranceTitle(type: EncumbranceType) {
		return `${startCase(getEncumbranceNameByType(type))} details`;
	}

	onSelectPriorityAfterClosing(
		selectedPriority: DropdownResult<DropdownOption<number>>,
	) {
		this.selectedPriorityAfterClosingName = selectedPriority.result.name;
		this.selectedPriorityAfterClosingNumber = selectedPriority.result.value;
		this.setPriorityAfterClosing(this.selectedPriorityAfterClosingNumber);
	}

	editPriority() {
		this.matDialog.open(ChangePriorityComponent, { panelClass: 'gradient' });
	}
}
